import {
  type DemoConnectedAccount,
  getAddedAccounts,
} from "./demoConnectionStore.ts";
import { DEMO_ACCOUNT_BY_ID } from "./seed/accounts.ts";
import { DEMO_BANKS } from "./seed/banks.ts";
import { DEMO_COMPANY_HOLDINGS_AG } from "./seed/identifiers.ts";

// Account-name key suffixes. The displayed (and persisted) name is resolved
// from the bilingual map below.
export const ACCOUNT_NAME_KEYS = [
  "currentAccount",
  "savingsAccount",
  "reserveAccount",
  "payrollAccount",
  "fxAccount",
  "pettyCash",
  "taxReserve",
  "operationsAccount",
];

// Demo-only account names. Resolved from this hard-coded bilingual map rather
// than i18n: the bank-connect screen is a standalone full-page load where the
// translation bundle arrives asynchronously, and these keys live only in the
// app's en.json (not cs.json, which is Lokalise-managed) — so under a Czech
// default the i18n lookup would surface raw keys. A static map keeps account
// names correct in any language, with no load race and no cs.json dependency.
const ACCOUNT_NAME_LABELS: Record<string, { cs: string; en: string }> = {
  currentAccount: { cs: "Běžný účet", en: "Current account" },
  savingsAccount: { cs: "Spořicí účet", en: "Savings account" },
  reserveAccount: { cs: "Rezervní účet", en: "Reserve account" },
  payrollAccount: { cs: "Mzdový účet", en: "Payroll account" },
  fxAccount: { cs: "Devizový účet", en: "FX account" },
  pettyCash: { cs: "Pokladna", en: "Petty cash" },
  taxReserve: { cs: "Daňová rezerva", en: "Tax reserve" },
  operationsAccount: { cs: "Provozní účet", en: "Operations account" },
};

/** Resolve a demo account-name key suffix to a label in the given language. */
export function accountNameLabel(suffix: string, language: string): string {
  const entry = ACCOUNT_NAME_LABELS[suffix];
  if (!entry) {
    return suffix;
  }
  return language.startsWith("cs") ? entry.cs : entry.en;
}

/** Deterministic pseudo-random so the same connection renders the same data. */
export function hashSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** 10-digit SCA authorization instruction number ("pokyn č."). */
export function makeScaInstructionNumber(connectionId: string): string {
  return (8000000000 + (hashSeed(connectionId) % 1999999999)).toString();
}

export type DemoConnectCurrency = DemoConnectedAccount["currency"];

// Banking basics per paymentProvider country (ISO 3166 alpha-3): IBAN prefix
// and the local account currency, so a Hungarian bank offers HU IBANs in HUF
// rather than Czech accounts in CZK.
const COUNTRY_BANKING: Record<string, { iban: string; currency: DemoConnectCurrency }> = {
  CZE: { iban: "CZ", currency: "CZK" },
  SVK: { iban: "SK", currency: "EUR" },
  AUT: { iban: "AT", currency: "EUR" },
  DEU: { iban: "DE", currency: "EUR" },
  POL: { iban: "PL", currency: "PLN" },
  BEL: { iban: "BE", currency: "EUR" },
  BGR: { iban: "BG", currency: "EUR" },
  HRV: { iban: "HR", currency: "EUR" },
  DNK: { iban: "DK", currency: "DKK" },
  EST: { iban: "EE", currency: "EUR" },
  FIN: { iban: "FI", currency: "EUR" },
  FRA: { iban: "FR", currency: "EUR" },
  GRC: { iban: "GR", currency: "EUR" },
  HUN: { iban: "HU", currency: "HUF" },
  ITA: { iban: "IT", currency: "EUR" },
  LVA: { iban: "LV", currency: "EUR" },
  LTU: { iban: "LT", currency: "EUR" },
  NLD: { iban: "NL", currency: "EUR" },
  NOR: { iban: "NO", currency: "NOK" },
  PRT: { iban: "PT", currency: "EUR" },
  ROU: { iban: "RO", currency: "RON" },
  SVN: { iban: "SI", currency: "EUR" },
  ESP: { iban: "ES", currency: "EUR" },
  SWE: { iban: "SE", currency: "SEK" },
};

// Approximate units of each currency per 1 CZK, to scale generated balances.
const UNITS_PER_CZK: Record<DemoConnectCurrency, number> = {
  CZK: 1,
  EUR: 1 / 25,
  PLN: 4.3 / 25,
  USD: 1.08 / 25,
  GBP: 1 / (25 * 1.17),
  HUF: 395 / 25,
  RON: 5 / 25,
  SEK: 11.2 / 25,
  DKK: 7.46 / 25,
  NOK: 11.6 / 25,
};

export function generateAccounts(params: {
  connectionId: string;
  bankName: string;
  bic: string;
  /** paymentProvider countryCode (ISO alpha-3); defaults to Czechia. */
  countryCode?: string;
  getAccountName: (keySuffix: string) => string;
}): DemoConnectedAccount[] {
  const banking = COUNTRY_BANKING[params.countryCode ?? "CZE"] ?? COUNTRY_BANKING.CZE;
  const isCzech = banking.iban === "CZ";
  const seed = hashSeed(params.connectionId);
  const count = 4 + (seed % 5); // 4–8 accounts
  const bankCode = (params.bic || "DEMO").slice(0, 4).toUpperCase();
  return Array.from({ length: count }, (_, index) => {
    // Mix the index multiplicatively so consecutive accounts get well-spread
    // balances/numbers instead of near-identical ones.
    const accSeed = (seed ^ ((index + 1) * 2654435761)) >>> 0;
    const number = (1000000000 + (accSeed % 8999999999)).toString();
    const balanceCzk = ((accSeed % 4900) + 60) * 1000; // 60k–4.96M CZK
    const balance =
      Math.round((balanceCzk * UNITS_PER_CZK[banking.currency]) / 100) * 100;
    return {
      id: `demo-connected-${params.connectionId}-${index}`,
      name: params.getAccountName(
        ACCOUNT_NAME_KEYS[index % ACCOUNT_NAME_KEYS.length],
      ),
      iban: `${banking.iban}${(accSeed % 90) + 10} ${bankCode} ${number.slice(0, 4)} ${number.slice(4, 8)} ${number.slice(8)}`.trim(),
      accountNumber: isCzech ? `${number}/0100` : number,
      currency: banking.currency,
      balance,
      bankName: params.bankName,
      bic: params.bic,
      companyId: DEMO_COMPANY_HOLDINGS_AG,
    };
  });
}

/**
 * The account being restored from the archive (or refreshed) via reconnect,
 * re-offered by the demo bank with the same IBAN and currency so the
 * assignment dialog recognises and pre-selects it.
 */
export function accountForReconnect(
  accountId: string,
  connectionId: string,
): DemoConnectedAccount | undefined {
  const seed = DEMO_ACCOUNT_BY_ID[accountId];
  if (seed) {
    const bank = DEMO_BANKS[seed.bankKey];
    return {
      id: `demo-connected-${connectionId}-restore`,
      name: seed.name,
      iban: seed.iban,
      accountNumber: seed.accountNumber,
      currency: seed.currency,
      balance: seed.balance,
      bankName: bank?.name ?? "",
      bic: bank?.bic ?? "",
      companyId: seed.companyId,
    };
  }
  const added = getAddedAccounts().find((a) => a.id === accountId);
  return added
    ? { ...added, id: `demo-connected-${connectionId}-restore` }
    : undefined;
}

export function formatBalance(amount: number, currency: string): string {
  return new Intl.NumberFormat("cs-CZ", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Pick a readable text colour (black or white) for a given background hex,
 * using the WCAG relative-luminance formula. Bank brand colours range from
 * very dark (J&T #1A1A1A) to very light (Raiffeisen #FEE600), so a fixed white
 * label is unreadable on the light ones — this chooses per background.
 */
export function getReadableTextColor(backgroundHex: string): "#000000" | "#FFFFFF" {
  const hex = backgroundHex.replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((char) => char + char)
          .join("")
      : hex;

  const red = parseInt(full.slice(0, 2), 16) / 255;
  const green = parseInt(full.slice(2, 4), 16) / 255;
  const blue = parseInt(full.slice(4, 6), 16) / 255;

  const toLinear = (channel: number) =>
    channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;

  const luminance =
    0.2126 * toLinear(red) +
    0.7152 * toLinear(green) +
    0.0722 * toLinear(blue);

  // Threshold 0.5 keeps mid-tones (e.g. KB crimson) on white text while
  // flipping bright yellows/cyans to black.
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
}
